import React from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { cx } from 'class-variance-authority';

const skeleton = tv({
  base: ' bg-[var(--bgGreyColor)] relative overflow-hidden after:absolute after:inset-0 after:translate-x-[-100%] after:bg-gradient-to-r after:from-transparent after:via-white/50 after:to-transparent after:animate-shimmer',
  variants: {
    width: {
      full: 'w-full',
      '1/2': 'w-1/2',
      '1/3': 'w-1/3',
      '2/3': 'w-2/3',
      '1/4': 'w-1/4',
      auto: 'w-auto',
    },
    height: {
      xs: 'h-3',
      sm: 'h-4',
      md: 'h-6',
      lg: 'h-10',
      xl: 'h-16',
      '2xl': 'h-24',
      '3xl': 'h-32',
      '4xl': 'h-48',
    },
    radius: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded-md',
      lg: 'rounded-lg',
      full: 'rounded-full',
    },
    type: {
      base: '',
      image: 'aspect-square rounded-md',
      avatar: 'rounded-full aspect-square',
      text: 'h-4 rounded-sm',
      title: 'h-6 rounded-sm',
      button: 'h-10 rounded-md',
      badge: 'h-5 w-16 rounded-full',
      card: 'rounded-lg',
      input: 'h-10 rounded-md',
      select: 'h-10 rounded-md',
    },
  },
  compoundVariants: [
    {
      type: 'image',
      class: 'aspect-video',
    },
    {
      type: 'avatar',
      class: 'w-10 h-10',
    },
    {
      type: 'button',
      class: 'w-24',
    },
    {
      type: 'card',
      class: 'w-full p-4 h-40',
    },
  ],
  defaultVariants: {
    width: 'full',
    height: 'md',
    radius: 'md',
    type: 'base',
  },
});

interface SkeletonProps extends Omit<VariantProps<typeof skeleton>, 'type'> {
  className?: string;
  type?: 'base' | 'image' | 'avatar' | 'text' | 'title' | 'button' | 'badge' | 'card' | 'input' | 'select';
  lines?: number;
  imageSize?: 'thumbnail' | 'preview' | 'full';
}

const SkeletonCommon: React.FC<SkeletonProps> = ({
  width,
  height,
  radius,
  className,
  type = 'base',
  lines = 1,
  imageSize,
  ...rest
}) => {
  if (type === 'text' && lines > 1) {
    return (
      <div className='flex flex-col space-y-2'>
        {[...Array(lines)].map((_, i) => (
          <div
            key={i}
            className={skeleton({
              width: i === lines - 1 ? '2/3' : 'full', // Last line is 2/3 width
              radius,
              type: 'text',
              className: cx(className),
            })}
            {...rest}
          />
        ))}
      </div>
    );
  }

  let imageClasses = '';
  if (type === 'image' && imageSize) {
    switch (imageSize) {
      case 'thumbnail':
        imageClasses = 'w-20 h-20';
        break;
      case 'preview':
        imageClasses = 'w-40 h-40';
        break;
      case 'full':
        imageClasses = 'w-full aspect-video';
        break;
    }
  }

  return (
    <div
      className={skeleton({
        width,
        height,
        radius,
        type,
        className: cx(className, imageClasses),
      })}
      {...rest}
    />
  );
};

export default SkeletonCommon;
